/* global google */
import {
    DrawingManagerF, GoogleMap, OverlayViewF, Polygon, useJsApiLoader,
} from '@react-google-maps/api';
import {
    CSSProperties, FC, useCallback, useEffect, useMemo, useRef,
} from 'react';
import { GOOGLE_MAPS_KEY } from '../../../const/environments.constants';
import { GOOGLE_LIBRARIES, googleMapsStyles } from '../../HybridMap';
import MapLoader from '../../HybridMap/MapLoader';
import { useCoverageContext } from './context/coverage.ctx';
import { getPolygonCenter, getAllCoveragesBounds, normalizePolygon } from './utils/polygon';

const DEFAULT_CENTER = { lng: -78.4678, lat: -0.1807 };
const DEFAULT_ZOOM = 11;

interface CoverageMapGoogleProps {
    pin?: { lng: number; lat: number } | null;
    flyTo?: { lng: number; lat: number } | null;
    onFlyToComplete?: () => void;
    onLoadError: () => void;
}

const polygonOptions = (isVisible: boolean, isSelected: boolean): google.maps.PolygonOptions => {
    let color = '#bfbfbf';

    if (isSelected) color = '#6C8EEF';
    else if (isVisible) color = '#52c41a';

    return {
        fillColor: color,
        fillOpacity: isSelected ? 0.4 : 0.25,
        strokeColor: color,
        strokeWeight: isSelected ? 3 : 2,
        clickable: true,
    };
};

const containerStyle: CSSProperties = { width: '100%', height: '100%' };

const CoverageMapGoogle: FC<CoverageMapGoogleProps> = ({
    pin, flyTo, onFlyToComplete, onLoadError,
}) => {
    const {
        coverages,
        selected,
        isDrawing,
        setSelected,
        handleDrawComplete,
    } = useCoverageContext();

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_KEY,
        libraries: GOOGLE_LIBRARIES,
    });

    const mapRef = useRef<google.maps.Map | null>(null);

    const initialCenter = useMemo(() => {
        if (coverages.length > 0) {
            const c = getPolygonCenter(coverages[0].polygon);
            return { lat: c.lat, lng: c.lng };
        }
        return { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng };
    }, [coverages]);

    useEffect(() => {
        if (loadError) onLoadError();
    }, [loadError, onLoadError]);

    const fitToAllCoverages = useCallback((map: google.maps.Map) => {
        const bounds = getAllCoveragesBounds(coverages);

        if (!bounds) return;

        const gBounds = new google.maps.LatLngBounds(
            { lat: bounds[0][1], lng: bounds[0][0] },
            { lat: bounds[1][1], lng: bounds[1][0] },
        );

        map.fitBounds(gBounds, 60);
    }, [coverages]);

    const handleLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
        fitToAllCoverages(map);
    }, [fitToAllCoverages]);

    // Fly to selected coverage center.
    useEffect(() => {
        if (!mapRef.current || !selected) return;

        const center = getPolygonCenter(selected.polygon);
        mapRef.current.panTo({ lat: center.lat, lng: center.lng });
        mapRef.current.setZoom(13);
    }, [selected]);

    // Fly to external coordinate.
    useEffect(() => {
        if (!mapRef.current || !flyTo) return;

        mapRef.current.panTo({ lat: flyTo.lat, lng: flyTo.lng });
        mapRef.current.setZoom(14);
        onFlyToComplete?.();
    }, [flyTo, onFlyToComplete]);

    const handleMapClick = useCallback(() => {
        if (isDrawing) return;
        // Click on the empty map area deselects. Polygon clicks are handled per-Polygon below.
        setSelected(null);
    }, [isDrawing, setSelected]);

    const handlePolygonComplete = useCallback((polygon: google.maps.Polygon) => {
        const path = polygon.getPath();
        const coords: [number, number][] = [];

        for (let i = 0; i < path.getLength(); i += 1) {
            const point = path.getAt(i);

            coords.push([point.lng(), point.lat()]);
        }

        // Close the ring like the Mapbox version (first point repeated at the end).
        if (coords.length > 0) coords.push(coords[0]);

        handleDrawComplete(coords);
        polygon.setMap(null);
    }, [handleDrawComplete]);

    if (loadError) return null;

    if (!isLoaded) {
        return <div style={containerStyle}><MapLoader /></div>;
    }

    return (
        <div style={containerStyle}>
            <GoogleMap
                center={initialCenter}
                zoom={DEFAULT_ZOOM}
                mapContainerStyle={containerStyle}
                options={{ fullscreenControl: false, streetViewControl: false, styles: googleMapsStyles }}
                onLoad={handleLoad}
                onClick={handleMapClick}
            >
                {coverages.map((c) => {
                    const normalized = normalizePolygon(c.polygon);
                    if (normalized.length < 3) return null;

                    const path = normalized.map(([lng, lat]) => ({ lat, lng }));
                    const isSelected = selected?._id === c._id;
                    const labelCenter = getPolygonCenter(c.polygon);

                    return (
                        <div key={c._id}>
                            <Polygon
                                paths={path}
                                options={polygonOptions(c.isVisible, isSelected)}
                                onClick={() => setSelected(c)}
                            />
                            <OverlayViewF
                                position={{ lat: labelCenter.lat, lng: labelCenter.lng }}
                                mapPaneName="overlayMouseTarget"
                            >
                                <div style={{
                                    transform: 'translate(-50%, -50%)',
                                    background: c.isVisible ? 'var(--color-purple)' : 'rgba(255,255,255,0.9)',
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: c.isVisible ? '#fff' : 'var(--color-dark)',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                }}
                                >
                                    {c.name}
                                </div>
                            </OverlayViewF>
                        </div>
                    );
                })}

                {pin && (
                    <OverlayViewF
                        position={{ lat: pin.lat, lng: pin.lng }}
                        mapPaneName="overlayMouseTarget"
                    >
                        <div style={{
                            transform: 'translate(-50%, -100%)',
                            width: 32,
                            height: 32,
                            background: 'var(--color-purple)',
                            borderRadius: '50% 50% 50% 0',
                            border: '3px solid white',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            // The mapbox version has -45deg rotation; reproduce here keeping the translate.
                            // Drop rotation if it conflicts with the translate origin.
                        }}
                        />
                    </OverlayViewF>
                )}

                {isDrawing && (
                    <DrawingManagerF
                        drawingMode={google.maps.drawing.OverlayType.POLYGON}
                        options={{
                            drawingControl: false,
                            polygonOptions: {
                                fillColor: '#6C8EEF',
                                fillOpacity: 0.3,
                                strokeColor: '#6C8EEF',
                                strokeWeight: 2,
                                editable: false,
                            },
                        }}
                        onPolygonComplete={handlePolygonComplete}
                    />
                )}
            </GoogleMap>
        </div>
    );
};

export default CoverageMapGoogle;
